import React, { useState, useEffect, useRef } from 'react';
import {
  FiMic,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiX,
  FiUploadCloud,
  FiAlertCircle,
  FiToggleLeft,
  FiToggleRight,
  FiLoader,
  FiLink,
  FiInfo,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { voiceService } from '../../services/apiService';

// ─── Add Artist Form ──────────────────────────────────────────────────────────

const AddArtistForm = ({ onCreated, onCancel }) => {
  // Two modes:
  //  'pvc'  — Admin already trained the voice via ElevenLabs dashboard (PVC), just enter the voice_id
  //  'ivc'  — Upload audio now for Instant Voice Clone (lower quality, good for testing)
  const [mode, setMode] = useState('pvc');

  // PVC mode fields
  const [voiceId, setVoiceId] = useState('');

  // IVC mode fields
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Shared fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const totalMB = files.reduce((sum, f) => sum + f.size / 1024 / 1024, 0).toFixed(1);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setFiles((prev) => {
      const combined = [...prev, ...selected];
      const seen = new Set();
      return combined.filter((f) => {
        if (seen.has(f.name)) return false;
        seen.add(f.name);
        return true;
      });
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Voice artist name is required.'); return; }

    setSaving(true);

    try {
      if (mode === 'pvc') {
        // PVC: admin provides the voice_id from ElevenLabs dashboard
        if (!voiceId.trim()) { setError('ElevenLabs Voice ID is required.'); setSaving(false); return; }
        const artist = await voiceService.createArtistFromVoiceId({
          name: name.trim(),
          description: description.trim(),
          elevenLabsVoiceId: voiceId.trim(),
        });
        toast.success(`Voice artist "${artist.name}" added!`);
        onCreated(artist);
      } else {
        // IVC: upload audio files — Instant Voice Clone
        if (files.length === 0) { setError('Upload at least one audio file.'); setSaving(false); return; }
        const formData = new FormData();
        formData.append('name', name.trim());
        formData.append('description', description.trim());
        files.forEach((f) => formData.append('files', f));

        const artist = await voiceService.createArtist(formData, (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        });
        toast.success(`Voice artist "${artist.name}" trained (IVC).`);
        onCreated(artist);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-violet-50 border border-violet-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <FiMic size={18} className="text-violet-600" />
        <h3 className="font-semibold text-gray-900">Add Voice Artist</h3>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 p-1 bg-white rounded-lg border border-violet-100">
        <button
          type="button"
          onClick={() => setMode('pvc')}
          className={`flex-1 text-xs py-2 rounded-md font-medium transition-colors ${
            mode === 'pvc'
              ? 'bg-violet-600 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Professional Voice Clone (PVC)
        </button>
        <button
          type="button"
          onClick={() => setMode('ivc')}
          className={`flex-1 text-xs py-2 rounded-md font-medium transition-colors ${
            mode === 'ivc'
              ? 'bg-violet-600 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Instant Clone (IVC — Test)
        </button>
      </div>

      {/* Mode explanation */}
      {mode === 'pvc' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 text-xs text-blue-800 leading-relaxed">
          <p className="font-semibold mb-1">Professional Voice Cloning (Recommended)</p>
          <p>Train the voice through the <strong>Voice AI dashboard</strong> first (requires 30+ minutes of clean audio, takes 3–7 business days). You will receive a Voice ID — paste it here.</p>
          <p className="mt-1 text-blue-600">Training does <strong>not</strong> consume character credits. Only audio generation does.</p>
          <a href="https://elevenlabs.io/app/voice-lab/professional-voice-cloning" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-blue-700 font-medium hover:underline">
            <FiLink size={11} /> Open Voice AI Dashboard →
          </a>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 leading-relaxed">
          <p className="font-semibold mb-1">Instant Voice Cloning — for testing only</p>
          <p>Upload audio now and an AI voice is created immediately. Works with as little as 3 minutes of audio. Lower quality than PVC — good for verifying the pipeline works before committing to PVC training.</p>
          <p className="mt-1">Training does <strong>not</strong> consume credits. Only generating audio does.</p>
        </div>
      )}

      {/* Shared fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Artist Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. రాజేష్ కుమార్"
            className="input w-full text-sm"
            disabled={saving}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Senior Anchor, Male"
            className="input w-full text-sm"
            disabled={saving}
          />
        </div>
      </div>

      {/* PVC: voice ID input */}
      {mode === 'pvc' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Voice ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            placeholder="e.g. pNInz6obpgDQGcFmaJgB"
            className="input w-full text-sm font-mono"
            disabled={saving}
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Find this in the Voice AI dashboard → Voices → your trained voice → Voice ID
          </p>
        </div>
      )}

      {/* IVC: file upload */}
      {mode === 'ivc' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Audio Samples <span className="text-red-500">*</span>
            <span className="text-gray-400 font-normal ml-1">(3+ min for testing; 30+ min for production quality)</span>
          </label>
          <div
            onClick={() => !saving && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              saving ? 'border-gray-200 cursor-not-allowed' : 'border-violet-300 hover:border-violet-400 cursor-pointer'
            }`}
          >
            <FiUploadCloud size={24} className="mx-auto text-violet-400 mb-1" />
            <p className="text-sm text-gray-600">Click to select audio files</p>
            <p className="text-xs text-gray-400 mt-0.5">mp3, wav, m4a — up to 100 MB per file, up to 10 files</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".mp3,.wav,.m4a,.ogg,.webm,.flac,audio/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={saving}
            />
          </div>

          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-100 text-xs">
                  <FiMic size={12} className="text-violet-400 flex-shrink-0" />
                  <span className="flex-1 truncate text-gray-700">{f.name}</span>
                  <span className="text-gray-400 flex-shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button type="button" onClick={() => removeFile(i)} disabled={saving} className="text-gray-400 hover:text-red-500">
                    <FiX size={12} />
                  </button>
                </div>
              ))}
              <p className="text-xs text-gray-500 px-1">
                {files.length} file{files.length !== 1 ? 's' : ''} · {totalMB} MB total
              </p>
            </div>
          )}

          {saving && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-xs text-violet-700">
                <FiLoader size={12} className="animate-spin" />
                {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Creating AI voice…'}
              </div>
              <div className="h-1.5 bg-violet-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(uploadProgress, 10)}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-600">
          <FiAlertCircle size={12} /> {error}
        </p>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="btn btn-primary text-sm flex items-center gap-1.5">
          {saving ? <FiLoader size={13} className="animate-spin" /> : <FiCheck size={13} />}
          {saving ? (mode === 'pvc' ? 'Adding…' : 'Training…') : (mode === 'pvc' ? 'Add Voice Artist' : 'Create IVC Voice')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="btn btn-ghost text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Artist Row ───────────────────────────────────────────────────────────────

const ArtistRow = ({ artist, onUpdated, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(artist.name);
  const [description, setDescription] = useState(artist.description || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const updated = await voiceService.updateArtist(artist._id, { name: name.trim(), description: description.trim() });
      toast.success('Voice artist updated.');
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const updated = await voiceService.updateArtist(artist._id, { isActive: !artist.isActive });
      onUpdated(updated);
    } catch (err) {
      toast.error('Status update failed.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await voiceService.deleteArtist(artist._id);
      toast.success(`"${artist.name}" deleted.`);
      onDeleted(artist._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className={`border rounded-xl p-4 transition-opacity ${artist.isActive ? '' : 'opacity-60'}`}>
      {editing ? (
        <div className="space-y-2">
          <input className="input w-full text-sm font-medium" value={name} onChange={(e) => setName(e.target.value)} placeholder="Artist name" />
          <input className="input w-full text-sm" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="btn btn-primary text-xs py-1.5 flex items-center gap-1">
              {saving ? <FiLoader size={11} className="animate-spin" /> : <FiCheck size={11} />} Save
            </button>
            <button onClick={() => setEditing(false)} className="btn btn-ghost text-xs py-1.5">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
            <FiMic size={18} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{artist.name}</span>
              <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${artist.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {artist.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {artist.description && <p className="text-xs text-gray-500 mt-0.5">{artist.description}</p>}
            <p className="text-[10px] text-gray-400 mt-1 font-mono">Voice ID: {artist.elevenLabsVoiceId}</p>
            <p className="text-[10px] text-gray-400">
              Added by {artist.createdBy?.name || 'admin'} · {new Date(artist.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={handleToggleActive} title={artist.isActive ? 'Deactivate' : 'Activate'} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
              {artist.isActive ? <FiToggleRight size={16} className="text-green-500" /> : <FiToggleLeft size={16} />}
            </button>
            <button onClick={() => setEditing(true)} title="Edit" className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
              <FiEdit2 size={15} />
            </button>
            {confirmDelete ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-red-600">Sure?</span>
                <button onClick={handleDelete} disabled={deleting} className="px-2 py-1 text-xs bg-red-600 text-white rounded-lg">{deleting ? '…' : 'Yes'}</button>
                <button onClick={() => setConfirmDelete(false)} className="px-2 py-1 text-xs bg-gray-100 rounded-lg">No</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50">
                <FiTrash2 size={15} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const VoiceArtistManager = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadArtists = async () => {
    try {
      const data = await voiceService.listAllArtists();
      setArtists(data);
    } catch (err) {
      toast.error('Failed to load voice artists.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadArtists(); }, []);

  const handleCreated = (newArtist) => {
    setArtists((prev) => [newArtist, ...prev]);
    setShowForm(false);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voice Artist Manager</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage voice artists for the Script to Voice feature.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary flex items-center gap-1.5 text-sm flex-shrink-0">
            <FiPlus size={15} /> Add Voice Artist
          </button>
        )}
      </div>

      {showForm && (
        <AddArtistForm
          onCreated={handleCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* PVC workflow guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 leading-relaxed">
        <p className="font-semibold mb-1.5 flex items-center gap-1.5"><FiInfo size={13} /> Professional Voice Cloning Workflow</p>
        <ol className="ml-3 list-decimal space-y-1">
          <li>Go to the <a href="https://elevenlabs.io/app/voice-lab/professional-voice-cloning" target="_blank" rel="noopener noreferrer" className="font-medium underline">Voice AI PVC Dashboard</a> and submit 30+ minutes of clean studio audio per artist</li>
          <li>The voice is trained (3–7 business days) and you receive an email when ready</li>
          <li>Open the dashboard → Voices → your trained voice → copy the <strong>Voice ID</strong></li>
          <li>Click <strong>"Add Voice Artist"</strong> above → select PVC mode → paste the Voice ID</li>
        </ol>
        <p className="mt-1.5 text-blue-600"><strong>Credit note:</strong> Training (PVC or IVC) does not consume character credits. Only generating audio does.</p>
        <p className="mt-0.5 text-blue-600"><strong>Test first:</strong> Use IVC mode with 3 min of audio to verify the pipeline works before submitting for PVC.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
          <FiLoader size={18} className="animate-spin" /> Loading voice artists…
        </div>
      ) : artists.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FiMic size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No voice artists yet. Click "Add Voice Artist" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {artists.map((artist) => (
            <ArtistRow
              key={artist._id}
              artist={artist}
              onUpdated={(u) => setArtists((prev) => prev.map((a) => (a._id === u._id ? u : a)))}
              onDeleted={(id) => setArtists((prev) => prev.filter((a) => a._id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
};
